import React, { useMemo } from "react"
import { Select } from "antd"

import Strings from "utils/strings"

import {
	EXTERNAL_BATCHING_STATUS_VALUES,
	getBatchingStatusLabel,
} from "../batchingStatus"
import type { ExternalBatchingStatus } from "../batchingStatus"

const { Option } = Select

/** Van filter value for routes that load at the warehouse instead of from a van. */
export const WAREHOUSE_PICKUP_VALUE = "warehouse"

export interface VanOption {
	vehicleId: number
	name?: string | null
}

interface ExternalDeliveryFiltersProps {
	selectedStatus: ExternalBatchingStatus[]
	/** Van vehicleIds (as strings) and/or WAREHOUSE_PICKUP_VALUE. */
	selectedVans: string[]
	vans: VanOption[]
	onStatusChange: (status: ExternalBatchingStatus[]) => void
	onVansChange: (vans: string[]) => void
	loading?: boolean
}

const ExternalDeliveryFilters: React.FC<ExternalDeliveryFiltersProps> = ({
	selectedStatus,
	selectedVans,
	vans,
	onStatusChange,
	onVansChange,
	loading = false,
}) => {
	const statusOptions = useMemo(
		() =>
			EXTERNAL_BATCHING_STATUS_VALUES.map((value) => ({
				value,
				label: getBatchingStatusLabel(value),
			})),
		[]
	)

	// Warehouse is always offered — bikes that load at the depot never appear in
	// the van list, so it is the only way to reach them.
	const vanOptions = useMemo(
		() => [
			{
				value: WAREHOUSE_PICKUP_VALUE,
				label: Strings.routes.pickupWarehouse,
			},
			...vans.map((v) => ({
				value: String(v.vehicleId),
				label:
					v.name ||
					(Strings.formatString(
						Strings.routes.vanWithId,
						String(v.vehicleId)
					) as string),
			})),
		],
		[vans]
	)

	return (
		<div className="ExternalDeliveryFilters">
			<Select
				mode="multiple"
				placeholder={Strings.routes.filterByStatus}
				value={selectedStatus}
				onChange={onStatusChange}
				allowClear
				disabled={loading}
				style={{ width: 230 }}
				maxTagCount="responsive"
			>
				{statusOptions.map((option) => (
					<Option key={option.value} value={option.value}>
						{option.label}
					</Option>
				))}
			</Select>
			<Select
				mode="multiple"
				placeholder={Strings.routes.filterByVan}
				value={selectedVans}
				onChange={onVansChange}
				allowClear
				disabled={loading}
				style={{ width: 230 }}
				maxTagCount="responsive"
			>
				{vanOptions.map((option) => (
					<Option key={option.value} value={option.value}>
						{option.label}
					</Option>
				))}
			</Select>
		</div>
	)
}

export default ExternalDeliveryFilters
